import type { NextPage } from 'next';

import type { Application } from '@src/@types/app';
import BottomBannerContainer from '@src/components/BottomBannerContainer';
import { PageContext } from '@src/config';
import { getPageListData } from '@src/config/getPageListData';
import { getSidebarBannerData } from '@src/config/getSidebarBannerData';
import type { IPageContextConfig } from '@src/config/PageContext';
import { MODES } from '@src/constants/filters';
import { ListKeys } from '@src/constants/microcredits';
import ProductList from '@src/containers/list/ProductList';
import { useBestProducts } from '@src/hooks/useBestProducts';
import { useListPage } from '@src/hooks/useListPage';
import { Layout } from 'app/Layout';
import { sravniOfferAction, fullDealOfferAction } from 'entities/Microcredits/lib/sravniOfferAction';
import { CardActionProvider } from 'entities/Microcredits/ui/CardActionProvider';
import { AmountFilter } from 'features/AmountFilter';
import { AnchorLinkList } from 'shared/ui';
import { BrandingHeaders } from 'widgets/BrandingHeaders';
import { FullDealEntryPoint } from 'widgets/FullDealEntryPoint';
import { useIsShowFullDealEntrypoint } from 'widgets/FullDealEntryPoint/lib';
import { LightweightCard } from 'widgets/LightweightCard';
import { MobileSubHeader } from 'widgets/MobileSubHeader';
import { SeoComponents } from 'widgets/SeoComponents';

import styles from './OnlinePage.module.scss';

const pageConfig: IPageContextConfig = {
  listKey: ListKeys.ONLINE,
  source: 'search',
  sortPanelVisible: true,
  seoLinksVisible: true,
  cardTermIsVisible: true,
  withBanners: true,
  withInvitationBanner: true,
  withEmailBurner: true,
  withSelectionBanner: true,
  withCardProductLink: true,
  withMonetizationCardLogoClick: true,
};

export const OnlinePage: NextPage = () => {
  const { filter, seo, pathname, hasUtmLabel } = useListPage(ListKeys.ONLINE);

  useBestProducts();

  const isShowFullDealEntrypoint = useIsShowFullDealEntrypoint();

  return (
    <Layout>
      <PageContext.Provider value={pageConfig}>
        <BrandingHeaders
          containerClassName={styles.page_header}
          afterHeaders={
            isShowFullDealEntrypoint ? (
              <FullDealEntryPoint heading={seo?.heading} />
            ) : (
              <AmountFilter filters={filter} listKey={ListKeys.ONLINE} />
            )
          }
        />

        {!hasUtmLabel && <AnchorLinkList />}

        <MobileSubHeader />
        <CardActionProvider rules={[sravniOfferAction, fullDealOfferAction]}>
          <ProductList
            gridLayout
            filter={filter}
            pathname={pathname}
            renderCard={(props) => <LightweightCard hasUtmLabel={hasUtmLabel} {...props} />}
          />
        </CardActionProvider>
        {!hasUtmLabel && seo ? (
          <SeoComponents
            seo={seo}
            breadcrumbs={seo.breadcrumbs}
            prefooter={seo.prefooter}
            showAnchorLinks={false}
            showSubheader={false}
          />
        ) : null}
        <BottomBannerContainer />
      </PageContext.Provider>
    </Layout>
  );
};

OnlinePage.getInitialProps = (ctx: Application.ReduxNextPageContext) =>
  Promise.all([
    getPageListData(ctx, { key: ListKeys.ONLINE, defaultFiltersMode: MODES.ONLINE }),
    getSidebarBannerData(ctx),
  ]);
